import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";

const fmtSEK = (v) =>
  v == null ? "—" : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(v);

const fmtDate = (d) =>
  new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const EMPTY = {
  effective_from: "",
  effective_to: "",
  fte_monthly_salary: "",
  employment_percentage: "100",
  pension_amount: "",
};

export default function EmploymentTermsPanel({ person, open, onClose }) {
  const { isAdmin } = useAuth();
  const [terms, setTerms] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [changing, setChanging] = useState(false);
  const [changeForm, setChangeForm] = useState({ employment_percentage: "", fte_monthly_salary: "", pension_amount: "", effective_from: "" });
  const [completing, setCompleting] = useState(null); // terms id being completed
  const [completeForm, setCompleteForm] = useState({ fte_monthly_salary: "", pension_amount: "" });
  const [deleting, setDeleting] = useState(null); // terms record pending delete confirmation

  const deleteTerms = async () => {
    if (!deleting) return;
    try {
      toast.dismiss();
      await api.delete(`/employment-terms/${deleting.id}`);
      toast.success("Employment terms deleted — timeline repaired");
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const completeTerms = async (t) => {
    const payload = {};
    if (t.fte_monthly_salary == null && completeForm.fte_monthly_salary !== "") {
      payload.fte_monthly_salary = Number(completeForm.fte_monthly_salary);
    }
    if (t.pension_amount == null && completeForm.pension_amount !== "") {
      payload.pension_amount = Number(completeForm.pension_amount);
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Nothing to complete");
      return;
    }
    try {
      toast.dismiss();
      await api.patch(`/employment-terms/${t.id}`, payload);
      toast.success("Initial terms completed");
      setCompleting(null);
      setCompleteForm({ fte_monthly_salary: "", pension_amount: "" });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const currentTerms =
    terms
      .filter((t) => t.effective_from <= today && (!t.effective_to || t.effective_to >= today))
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] || null;

  const load = async () => {
    if (!person) return;
    const res = await api.get(`/people/${person.id}/employment-terms`);
    setTerms(res.data);
  };

  useEffect(() => {
    if (open && person) {
      load().catch((e) => toast.error(apiError(e)));
      setAdding(false);
      setChanging(false);
      setForm(EMPTY);
      setChangeForm({ employment_percentage: "", fte_monthly_salary: "", pension_amount: "", effective_from: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person?.id]);

  const add = async () => {
    try {
      toast.dismiss();
      await api.post(`/people/${person.id}/employment-terms`, {
        effective_from: derivedFrom || form.effective_from,
        effective_to: derivedFrom ? derivedTo : form.effective_to || null,
        fte_monthly_salary: form.fte_monthly_salary === "" ? null : Number(form.fte_monthly_salary),
        employment_percentage: Number(form.employment_percentage),
        pension_amount: form.pension_amount === "" ? null : Number(form.pension_amount),
      });
      toast.success("Employment terms added");
      setAdding(false);
      setForm(EMPTY);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changePercentage = async () => {
    // Client-side guard mirroring the backend rule — avoids a 422 round-trip.
    if (changeForm.effective_from && changeForm.effective_from.slice(8, 10) !== "01") {
      toast.dismiss();
      toast.error("Employment percentage changes must take effect from the first day of a month.");
      return;
    }
    try {
      toast.dismiss();
      const payload = { effective_from: changeForm.effective_from };
      if (changeForm.employment_percentage !== "") payload.employment_percentage = Number(changeForm.employment_percentage);
      if (changeForm.fte_monthly_salary !== "") payload.fte_monthly_salary = Number(changeForm.fte_monthly_salary);
      if (changeForm.pension_amount !== "") payload.pension_amount = Number(changeForm.pension_amount);
      await api.post(`/people/${person.id}/employment-terms/change`, payload);
      toast.success("Employment terms changed — history preserved");
      setChanging(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sortedAsc = [...terms].sort((a, b) => a.effective_from.localeCompare(b.effective_from));
  // Delete is explicit admin data correction: ANY record (latest/middle/initial/only) may be
  // deleted. Deleting the initial or only record leaves an explicitly incomplete state that is
  // repaired through the controlled "Add initial terms" action below (dates auto-derived).

  const earliest = sortedAsc[0] || null;
  // Controlled historical correction: "Add initial terms" only when the initial employment
  // period is uncovered (Person.start_date before the earliest existing terms, or no terms).
  // Dates are auto-derived — the admin enters only the economic values.
  const hasUncoveredInitial = !!person?.start_date && (!earliest || person.start_date < earliest.effective_from);
  const showAddInitial = isAdmin && !adding && (hasUncoveredInitial || terms.length === 0);
  const derivedFrom = hasUncoveredInitial ? person.start_date : null;
  const derivedTo =
    hasUncoveredInitial && earliest
      ? new Date(new Date(`${earliest.effective_from}T00:00:00`).getTime() - 86400000).toISOString().slice(0, 10)
      : null;
  const manualDates = terms.length === 0 && !person?.start_date; // rare fallback: nothing to derive from

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto" data-testid="employment-terms-panel">
        <SheetHeader>
          <SheetTitle className="font-heading">Employment Terms</SheetTitle>
          <SheetDescription>
            {person?.name} — effective-dated. Historical terms are never overwritten.
          </SheetDescription>
        </SheetHeader>

        {currentTerms && (
          <div className="mt-4 bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex items-center justify-between" data-testid="current-percentage-banner">
            <div>
              <div className="text-xs text-slate-500">Current Employment Percentage</div>
              <div className="text-lg font-bold text-slate-900" data-testid="current-percentage-value">{currentTerms.employment_percentage} %</div>
            </div>
            {isAdmin && !changing && (
              <Button
                size="sm"
                className="bg-[#0052CC] hover:bg-[#0047B3]"
                onClick={() => {
                  setChanging(true);
                  setAdding(false);
                  setChangeForm({ employment_percentage: "", fte_monthly_salary: "", pension_amount: "", effective_from: "" });
                }}
                data-testid="change-percentage-button"
              >
                Change terms (salary / % / pension)
              </Button>
            )}
          </div>
        )}

        {isAdmin && changing && (
          <div className="mt-4 border border-slate-200 rounded-lg p-4 space-y-3" data-testid="change-percentage-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>New FTE salary (unchanged if empty)</Label>
                <Input type="number" min="0" value={changeForm.fte_monthly_salary} onChange={(e) => setChangeForm({ ...changeForm, fte_monthly_salary: e.target.value })} data-testid="change-salary-input" />
              </div>
              <div className="space-y-1.5">
                <Label>New employment % (unchanged if empty)</Label>
                <Input type="number" min="1" max="100" value={changeForm.employment_percentage} onChange={(e) => setChangeForm({ ...changeForm, employment_percentage: e.target.value })} data-testid="change-percentage-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>New pension (unchanged if empty)</Label>
                <Input type="number" min="0" value={changeForm.pension_amount} onChange={(e) => setChangeForm({ ...changeForm, pension_amount: e.target.value })} data-testid="change-pension-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Effective from (first of month)</Label>
                <Input type="date" value={changeForm.effective_from} onChange={(e) => setChangeForm({ ...changeForm, effective_from: e.target.value })} data-testid="change-percentage-effective-from" />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              History is preserved: the current period closes the day before the new one starts. Fields left empty carry forward unchanged. Changes must take effect from the first day of a month.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setChanging(false)} data-testid="change-percentage-cancel">Cancel</Button>
              <Button onClick={changePercentage} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="change-percentage-save">Apply change</Button>
            </div>
          </div>
        )}

        <div className="mt-6 space-y-3" data-testid="employment-terms-list">
          {terms.map((t) => (
            <div key={t.id} className="border border-slate-200 rounded-lg p-4" data-testid={`terms-item-${t.id}`}>
              <div className="text-sm font-semibold text-slate-900">
                {t.effective_from} → {t.effective_to || "ongoing"}
              </div>
              <div className="grid grid-cols-3 gap-3 mt-2 text-sm">
                <div>
                  <div className="text-xs text-slate-500">Salary (100% FTE)</div>
                  <div className="font-semibold text-slate-800">{fmtSEK(t.fte_monthly_salary)} SEK</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Employment</div>
                  <div className="font-semibold text-slate-800">{t.employment_percentage} %</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Pension</div>
                  <div className="font-semibold text-slate-800">{t.pension_amount == null ? "—" : `${fmtSEK(t.pension_amount)} SEK`}</div>
                </div>
              </div>
              {isAdmin && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <button
                    className="text-xs font-semibold text-[#B91C1C] hover:underline"
                    onClick={() => setDeleting(t)}
                    data-testid={`delete-terms-${t.id}`}
                  >
                    Delete terms
                  </button>
                </div>
              )}
              {isAdmin && (t.fte_monthly_salary == null || t.pension_amount == null) && (
                <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`terms-complete-section-${t.id}`}>
                  {completing === t.id ? (
                    <div className="space-y-3">
                      <p className="text-xs text-slate-500">
                        Complete the missing initial values on this record — the effective-from date stays {t.effective_from}. Once set, later changes use the effective-dated change workflow.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {t.fte_monthly_salary == null && (
                          <div className="space-y-1.5">
                            <Label>FTE salary</Label>
                            <Input type="number" min="0" value={completeForm.fte_monthly_salary} onChange={(e) => setCompleteForm({ ...completeForm, fte_monthly_salary: e.target.value })} data-testid={`complete-salary-${t.id}`} />
                          </div>
                        )}
                        {t.pension_amount == null && (
                          <div className="space-y-1.5">
                            <Label>Pension</Label>
                            <Input type="number" min="0" value={completeForm.pension_amount} onChange={(e) => setCompleteForm({ ...completeForm, pension_amount: e.target.value })} data-testid={`complete-pension-${t.id}`} />
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setCompleting(null)} data-testid={`complete-cancel-${t.id}`}>Cancel</Button>
                        <Button size="sm" className="bg-[#0052CC] hover:bg-[#0047B3]" onClick={() => completeTerms(t)} data-testid={`complete-save-${t.id}`}>Complete</Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="text-xs font-semibold text-[#0052CC] hover:underline"
                      onClick={() => {
                        setCompleting(t.id);
                        setCompleteForm({ fte_monthly_salary: "", pension_amount: "" });
                      }}
                      data-testid={`complete-terms-${t.id}`}
                    >
                      Complete missing salary/pension
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          {terms.length === 0 && (
            <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3 flex gap-2" data-testid="terms-incomplete">
              <AlertTriangle className="h-4 w-4 text-[#B45309] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-slate-900">Employment Terms incomplete</div>
                <p className="text-xs text-slate-600 mt-0.5">
                  This employee has no Employment Terms. Personnel Costs and Resource Planning show N/A until initial terms are added — no values are assumed.
                </p>
              </div>
            </div>
          )}
        </div>

        {showAddInitial && (
          <div className="mt-4">
            {hasUncoveredInitial && terms.length > 0 && (
              <div className="mb-3 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3 flex gap-2" data-testid="terms-uncovered-initial">
                <AlertTriangle className="h-4 w-4 text-[#B45309] shrink-0 mt-0.5" />
                <p className="text-xs text-slate-600">
                  Initial period {fmtDate(person.start_date)} → {fmtDate(derivedTo)} is not covered by any Employment Terms. Personnel Costs and Resource Planning show N/A for this period.
                </p>
              </div>
            )}
            <Button onClick={() => setAdding(true)} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="add-terms-button">
              Add initial terms
            </Button>
            <p className="text-xs text-slate-500 mt-1.5">
              Creates the initial Employment Terms from the factual employment start date
              {person?.start_date ? ` (${fmtDate(person.start_date)})` : ""}. Later changes use "Change terms"; corrections of mistakes use "Delete terms".
            </p>
          </div>
        )}

        {isAdmin && adding && (
          <div className="mt-4 border border-slate-200 rounded-lg p-4 space-y-3" data-testid="terms-form">
            {manualDates ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Effective from</Label>
                  <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="terms-effective-from" />
                </div>
                <div className="space-y-1.5">
                  <Label>Effective to (optional)</Label>
                  <Input type="date" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })} data-testid="terms-effective-to" />
                </div>
              </div>
            ) : (
              <div className="bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3" data-testid="terms-derived-period">
                <div className="text-xs text-slate-500">Initial period — derived from the employment start date</div>
                <div className="text-sm font-semibold text-slate-900 mt-0.5" data-testid="terms-derived-period-value">
                  {derivedFrom ? `${fmtDate(derivedFrom)} → ${derivedTo ? fmtDate(derivedTo) : "ongoing"}` : "—"}
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>FTE salary (optional until Build 4)</Label>
                <Input type="number" min="0" value={form.fte_monthly_salary} onChange={(e) => setForm({ ...form, fte_monthly_salary: e.target.value })} data-testid="terms-salary" />
              </div>
              <div className="space-y-1.5">
                <Label>Employment %</Label>
                <Input type="number" min="1" max="100" value={form.employment_percentage} onChange={(e) => setForm({ ...form, employment_percentage: e.target.value })} data-testid="terms-percentage" />
              </div>
              <div className="space-y-1.5">
                <Label>Pension (optional)</Label>
                <Input type="number" min="0" value={form.pension_amount} onChange={(e) => setForm({ ...form, pension_amount: e.target.value })} data-testid="terms-pension" />
              </div>
            </div>
            <p className="text-xs text-slate-500">Pension is manually maintained and never auto-scaled.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAdding(false)} data-testid="terms-cancel">Cancel</Button>
              <Button onClick={add} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="terms-save">Save</Button>
            </div>
          </div>
        )}

        <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
          <AlertDialogContent data-testid="delete-terms-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete employment terms?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  {deleting && (
                    <div className="text-sm text-slate-700 space-y-1" data-testid="delete-terms-details">
                      <div>Effective from: <span className="font-semibold">{fmtDate(deleting.effective_from)}</span></div>
                      <div>Salary: <span className="font-semibold">{fmtSEK(deleting.fte_monthly_salary)} SEK</span></div>
                      <div>Employment: <span className="font-semibold">{deleting.employment_percentage} %</span></div>
                      <div>Pension: <span className="font-semibold">{fmtSEK(deleting.pension_amount)} SEK</span></div>
                    </div>
                  )}
                  <p>This record will be permanently removed. The surrounding timeline will be repaired automatically.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-terms-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-[#B91C1C] hover:bg-[#991B1B]" onClick={deleteTerms} data-testid="delete-terms-confirm">
                Delete terms
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
